// Ô chọn khách hàng (F4): chỉ tìm theo tên / SĐT và chọn khách có sẵn; để trống = Khách lẻ (không tạo khách mới tại POS).
// remote: tìm khách trên hệ thống (POS bán hàng); mặc định tìm trong danh sách khách mẫu của POS (module liệu trình)
import React, { useEffect, useState } from 'react';
import { Crown, Loader2, User, X } from 'lucide-react';
import type { Customer } from '../data';
import usePosStore from '../store';
import { searchCustomersRemote } from '../remote';
import { foldVi, formatPhone, money } from '../utils';

export const TIER_COLOR: Record<Customer['tier'], string> = {
  'Thường': 'text-slate-500',
  'Bạc': 'text-slate-500',
  'Vàng': 'text-amber-500',
  'Kim cương': 'text-sky-500',
};

interface Props {
  value: Customer | null;
  onChange: (c: Customer | null) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  debtOf: (customerId: string) => number;
  /** Nhãn nhỏ góc phải ô khách (VD: đơn nháp đang mở) – gộp vào đây để nhường chỗ cho giỏ hàng */
  badge?: React.ReactNode;
  /** true: tìm khách trên hệ thống (API khách hàng) */
  remote?: boolean;
}

export default function CustomerPicker({ value, onChange, inputRef, debtOf, badge, remote = false }: Props) {
  const customers = usePosStore(s => s.customers);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [results, setResults] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);

  const q = query.trim();
  const digits = q.replace(/\D/g, '');
  const isPhone = digits.length > 0 && digits.length === q.replace(/\s/g, '').length;
  // Tối thiểu 3 ký tự, hoặc số điện thoại từ 3 số
  const ready = isPhone ? digits.length >= 3 : q.length >= 3;
  // Tìm trên hệ thống sau khi ngừng gõ (bỏ kết quả của lần gõ cũ trả về muộn); khách mẫu thì lọc ngay
  useEffect(() => {
    if (!ready) { setResults([]); setSearching(false); return; }
    if (!remote) {
      const nq = foldVi(q);
      setResults(customers.filter(c => (isPhone ? c.phone.includes(digits) : foldVi(c.name).includes(nq))).slice(0, 8));
      return;
    }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      searchCustomersRemote(isPhone ? digits : q)
        .then(list => { if (alive) { setResults(list); setFailed(false); setActive(0); } })
        .catch(() => { if (alive) { setResults([]); setFailed(true); } })
        .finally(() => { if (alive) setSearching(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [q, digits, isPhone, ready, remote, customers]);

  const select = (c: Customer | null) => {
    onChange(c);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || !ready) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[active]) select(results[active]);
    } else if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
  };

  if (value) {
    const debt = debtOf(value.id);
    return (
      <div className="flex min-h-[44px] items-center gap-2 rounded-lg border border-[#3e91ab]/40 bg-white px-3 py-1.5">
        <User className="h-5 w-5 shrink-0 text-[#3e91ab]" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 text-sm">
            <span className="font-bold text-slate-800">{value.name}</span>
            <span className="text-slate-500">{formatPhone(value.phone)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
            {/* Khách từ hệ thống: API tìm khách chưa trả hạng / công nợ nên chỉ hiện mã khách hàng */}
            {value.id.startsWith('api-') ? (
              <span>Mã KH <b className="font-mono text-slate-700">{value.source || value.id.slice(4)}</b></span>
            ) : (<>
              <span className={`flex items-center gap-0.5 font-semibold ${TIER_COLOR[value.tier]}`}><Crown className="h-3 w-3" />{value.tier}</span>
              <span>·</span>
              <span>Nợ <b className={debt > 0 ? 'text-red-600' : 'text-slate-700'}>{money(debt)}đ</b></span>
              {value.prepaid > 0 && <><span>·</span><span>Ví <b className="text-slate-700">{money(value.prepaid)}đ</b></span></>}
            </>)}
          </div>
        </div>
        {badge}
        <button onClick={() => { onChange(null); setTimeout(() => inputRef?.current?.focus(), 0); }} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-red-500" aria-label="Bỏ chọn khách">
          <X className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center"><User className="h-4 w-4 text-slate-400" /></span>
      <input
        ref={inputRef}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder="Khách lẻ · tìm SĐT / tên (F4)"
        aria-label="Tìm khách hàng theo SĐT hoặc tên"
        className={`h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 text-sm outline-none focus:border-[#3e91ab] focus:ring-2 focus:ring-[#3e91ab]/20 ${badge ? 'pr-48' : 'pr-3'}`}
      />
      {badge && <span className="absolute inset-y-0 right-2 flex items-center">{badge}</span>}
      {open && q.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
          {!ready ? (
            <div className="px-3 py-3 text-sm text-slate-500">Nhập tối thiểu 3 ký tự hoặc 3 số điện thoại…</div>
          ) : searching ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Đang tìm khách…</div>
          ) : failed ? (
            <div className="px-3 py-3 text-sm text-red-600">Không tìm được khách – kiểm tra kết nối rồi gõ lại.</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2.5 text-sm text-slate-500">
              Không tìm thấy khách “{q}”
              <span className="mt-0.5 block text-xs text-slate-400">Kiểm tra lại SĐT / tên, hoặc để trống để bán cho khách lẻ.</span>
            </div>
          ) : results.map((c, i) => (
            <button
              key={c.id}
              onMouseDown={e => e.preventDefault()}
              onClick={() => select(c)}
              onMouseEnter={() => setActive(i)}
              className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left ${active === i ? 'bg-[#ecf6f9]' : ''}`}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-800">{c.name}</span>
                <span className="text-xs text-slate-500">{formatPhone(c.phone)}</span>
              </span>
              {remote ? (
                c.source && <span className="shrink-0 font-mono text-xs text-slate-500">{c.source}</span>
              ) : (
                <span className="shrink-0 text-right text-xs">
                  <span className={`block font-semibold ${TIER_COLOR[c.tier]}`}>{c.tier}</span>
                  {debtOf(c.id) > 0 && <span className="text-red-600">Nợ {money(debtOf(c.id))}</span>}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
